import { createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
export const initialSkillState = {
  title: "",
  description: "",
};

export const initialAssignWorkerState = {
  name: "",
  aka_name: "",
  phone: "",
  staff_email: "",
  address: "",
  driver_licence: "",
  licence_expiry_date: "",
  ni_number: null,
  checked_date: "",
  checked_by: "",
  skills: [],
  sort_order: null,
  contract_type: "",
  wage_rate: null,
  overtime_rate: null,
  emergency_contact_name: "",
  emergency_contact_relation: "",
  emergency_contact_number: "",
  notes: "",
};

export const initialAbsenceState = {
  worker_id: "",
  reason: "",
  start_date: "",
  end_date: "",
  notified_date: "",
  authorised_date: "",
  authorised_by: "",
};

export const initialCreateTaskState = {
  crews: [
    {
      id: "",
      name: "",
      date: "",
      foreman_id: "",
      assets: [
        {
          id: "",
          type: "",
        },
      ],
      tasks: [
        {
          id: "",
          name: "",
        },
      ],
    },
  ],
  unavailable: [
    {
      id: "",
      type: "",
      date: "",
    },
  ],
  inyard: [
    {
      id: "",
      type: "",
      date: "",
    },
  ],
};

const initialState = {
  skills: [],
  operationStaffs: [],
  operationStaffPagination: {},
  assignedWorkers: [],
  staffAbsences: [],
  operationTasks: [],
  taskCrewList: {} as { [key: string]: any },
  taskPagination: {},
  singleOperationStaff: {},
  singleStaffAbsence: {},
  addSkillResponse: false,
  addStaffAbsenceResponse: false,
  addOperationStaffResponse: false,
  deleteOperationStaffResponse: false,
  deleteSkillResponse: false,
  addAssignWorkerResponse: false,
  removeAssignWorkerResponse: false,
  deleteStaffAbsenceResponse: false,
  createTaskResponse: false,
  resetCreateTaskResponse: false,
  trackWorkerDetails: {},
  addTrackWorkerResponse: false,
};

const assignWorkerSlice = createSlice({
  name: "assignWorker",
  initialState,
  reducers: {
    getAllSkillAction: (state) => {},
    setAllSkillAction: (state, { payload }) => {
      state.skills = payload;
    },

    addSkillAction: (state, { payload }) => {},
    setAddSkillAction: (state, { payload }) => {
      state.addSkillResponse = payload;
    },

    deleteSkillAction: (state, { payload }) => {},
    setDeleteSkillAction: (state, { payload }) => {
      state.deleteSkillResponse = payload;
    },

    getAllOperationStaffAction: (state, { payload }) => {},
    setAllOperationStaffAction: (state, { payload }) => {
      state.operationStaffs = payload?.list;
      state.operationStaffPagination = payload?.pagination;
    },

    addOperationStaffAction: (state, { payload }) => {},
    setAddOperationStaffAction: (state, { payload }) => {
      state.addOperationStaffResponse = payload;
    },

    getSingleOperationStaffAction: (state, { payload }) => {},
    setSingleOperationStaffAction: (state, { payload }) => {
      state.singleOperationStaff = payload;
    },

    deleteOperationStaffAction: (state, { payload }) => {},
    setDeleteOperationStaffAction: (state, { payload }) => {
      state.deleteOperationStaffResponse = payload;
    },

    getAllAssignedWorkerListAction: (state, { payload }) => {},
    setAllAssignedWorkerListAction: (state, { payload }) => {
      state.assignedWorkers = payload;
    },

    addAssignWorkerAction: (state, { payload }) => {},
    setAddAssignWorkerAction: (state, { payload }) => {
      state.addAssignWorkerResponse = payload;
    },

    deleteAssignWorkerAction: (state, { payload }) => {},
    setDeleteAssignWorkerAction: (state, { payload }) => {
      state.removeAssignWorkerResponse = payload;
    },

    // absence reducers can be added here
    getAllStaffAbsenceAction: (state, { payload }) => {},
    setAllStaffAbsenceAction: (state, { payload }) => {
      state.staffAbsences = payload?.list;
    },

    addStaffAbsenceAction: (state, { payload }) => {},
    setAddStaffAbsenceAction: (state, { payload }) => {
      state.addStaffAbsenceResponse = payload;
    },

    getSingleStaffAbsenceAction: (state, { payload }) => {},
    setSingleStaffAbsenceAction: (state, { payload }) => {
      state.singleStaffAbsence = payload;
    },

    deleteStaffAbsenceAction: (state, { payload }) => {},
    setDeleteStaffAbsenceAction: (state, { payload }) => {
      state.deleteStaffAbsenceResponse = payload;
    },
    // operation tasks reducers can be added here
    getAllOperationTasksAction: (state, { payload }) => {},
    setAllOperationTasksAction: (state, { payload }) => {
      const { pagination = { currentPage: 1 }, list = [] } = payload;
      state.operationTasks = list;
      state.taskPagination = pagination;
    },

    getAllTaskCrewListAction: (state, { payload }) => {},
    setAllTaskCrewListAction: (state, { payload }) => {
      const tz: string = payload.timezone || "UTC";
      let crews = payload.crews || [];
      console.log("crews-189", crews);
      const taskCount = state?.operationTasks?.length || 0;
      // Case 1: No crews from API → create crews based on operationTasks
      if (!crews.length && taskCount) {
        crews = (state.operationTasks as any[]).map((task, idx: number) => ({
          id: "",
          name: `Crew ${idx + 1}`,
          date: "",
          foreman_id: "",
          drivers: [],
          assets: [],
          tasks: [
            {
              id: (task as any).job_id,
              name: (task as any).job_title,
              type: "TASK",
              customer_name: (task as any).customer_name,
              start_date_time:
                (task as any).start_date_time || (task as any).start_time,
              end_date_time:
                (task as any).end_date_time || (task as any).end_time,
              collection_addresses: (task as any).collection_addresses,
              delivery_addresses: (task as any).delivery_addresses,
            },
          ],
        }));
      }

      // Case 2: If crews < operationTasks → add missing crews
      if (crews.length < taskCount) {
        const missingCount = taskCount - crews.length;
        const existingTaskIds = crews.flatMap((c: any) =>
          (c.tasks || []).map((t: any) => t.id)
        );

        const unassignedTasks = (state.operationTasks as any[]).filter(
          (task) => !existingTaskIds.includes((task as any).job_id)
        );

        for (let i = 0; i < missingCount; i++) {
          const task = unassignedTasks[i];

          // Find highest existing crew number
    const existingCrewNumbers = crews
      .map((c: any) => {
        const match = c?.name?.match(/\d+/);
        return match ? Number(match[0]) : 0;
      })
      .filter(Boolean);

    const nextCrewNumber =
      existingCrewNumbers.length > 0
        ? Math.max(...existingCrewNumbers) + 1
        : 1;

          crews.push({
            id: "",
            name: `Crew ${nextCrewNumber}`,
            date: "",
            foreman_id: "",
            drivers: [],
            assets: [],
            tasks: task
              ? [
                  {
                    id: (task as any).job_id,
                    name: (task as any).job_title,
                    type: "TASK",
                    customer_name: (task as any).customer_name,
                    start_date_time:
                      (task as any).start_date_time || (task as any).start_time,
                    end_date_time:
                      (task as any).end_date_time || (task as any).end_time,
                    collection_addresses: (task as any).collection_addresses,
                    delivery_addresses: (task as any).delivery_addresses,
                  },
                ]
              : [],
          });
        }
      }

      // Now build columns
      const columns: any = {
        vehicles: {
          name: "Vehicles",
          section: "tabs",
          items: (payload.vehicle || []).map((v: any) => ({
            id: v.id,
            content: v.name,
            type: "VEHICLE",
          })),
        },
        drivers: {
          name: "Drivers",
          section: "tabs",
          items: (payload.driver || []).map((d: any) => ({
            id: d.id,
            content: d.name,
            skills: d.skills || [],
            type: "DRIVER",
          })),
        },
        portal: {
          name: "Porter",
          section: "tabs",
          items: (payload.porter || []).map((p: any) => ({
            id: p.id,
            content: p.name,
            skills: p.skills || [],
            type: "PORTER",
          })),
        },
        unavailable: {
          name: "Unavailable",
          section: "status",
          items: (payload.unavailable || []).map((u: any) => ({
            id: u.id,
            content: u.name || u.type,
            type: u.type,
          })),
        },
        inYard: {
          name: "In Yard",
          section: "status",
          items: (payload.inyard || []).map((y: any) => ({
            id: y.id,
            content: y.name || y.type,
            type: y.type,
          })),
        },

        // crews setup
        ...Object.fromEntries(
          crews.map((crew: any, idx: number) => [
            `crew${idx + 1}`,
            {
              id: crew?.id || null,
              name: crew.name || `Crew ${idx + 1}`,
              section: "crew",
              foreman_id: crew.foreman_id || "",
              items: [
                ...(crew.drivers || []).map((d: any) => ({
                  id: d.id,
                  content: d.name,
                  skills: d.skills || [],
                  type: d?.type || "DRIVER",
                })),
                ...(crew.assets || []).map((a: any) => ({
                  id: a.id,
                  content: a.name,
                  type: a?.type || "VEHICLE",
                })),
                ...(crew.tasks || []).map((t: any) => {
                  const formatTime = (val?: string) => {
                    if (!val) return "";

                    // If value already looks like a time (HH:mm or HH:mm:ss)
                    if (/^\d{2}:\d{2}(:\d{2})?$/.test(val)) {
                      return val.slice(0, 5); // keep "HH:mm"
                    }

                    // Otherwise assume it's a datetime
                    return dayjs(val).isValid()
                      ? dayjs(val).format("HH:mm")
                      : "";
                  };

                  return {
                    id: t.id,
                    content: t.name,
                    customer_name: t.customer_name,
                    type: "TASK",
                    start_time: t.type==='TASK' ? formatTime(t.start_date_time) : dayjs.utc(t.start_date_time).tz(tz).format("HH:mm") || formatTime(t.start_time),
                    end_time: t.type==='TASK' ? formatTime(t.end_date_time) : dayjs.utc(t.end_date_time).tz(tz).format("HH:mm") || formatTime(t.end_time),
                    name: t.name,
                    fromPostCode: t?.collection_addresses?.[0]?.postcode || "",
                    toPostCode: t?.delivery_addresses?.[0]?.postcode || "",
                  };
                }),
              ],
            },
          ])
        ),
      };

      console.log("columns-351", columns);
      state.taskCrewList = columns;
    },

    addBlankCrewAction: (state) => {
      // find how many crews already exist
      const crewKeys = Object.keys(state.taskCrewList || {}).filter((key) =>
        key.startsWith("crew")
      );
      const newIndex = crewKeys.length + 1;

      // build a new crew column
      const newCrewKey = `crew${newIndex}`;
      const newCrew = {
        id: null,
        name: `Crew ${newIndex}`,
        section: "crew",
        foreman_id: "",
        items: [], // blank crew = no drivers, no assets, no tasks
      };

      // add to state
      state.taskCrewList[newCrewKey] = newCrew;
    },

    createTaskCrewAction: (state, { payload }) => {},
    setCreateTaskCrewAction: (state, { payload }) => {
      state.createTaskResponse = payload;
    },

    resetTaskCrewStateAction: (state, { payload }) => {},
    setResetTaskCrewStateAction: (state, { payload }) => {
      state.resetCreateTaskResponse = payload;
    },

    // track worker details
    getTrackWorkerDetailsAction: (state, { payload }) => {},
    setTrackWorkerDetailsAction: (state, { payload }) => {
      state.trackWorkerDetails = payload;
    },

    addTrackWorkerAction: (state, { payload }) => {},
    setAddTrackWorkerAction: (state, { payload }) => {
      state.addTrackWorkerResponse = payload;
    },
  },
});

export const {
  getAllSkillAction,
  setAllSkillAction,
  addSkillAction,
  setAddSkillAction,
  deleteSkillAction,
  setDeleteSkillAction,
  getAllOperationStaffAction,
  setAllOperationStaffAction,
  addOperationStaffAction,
  setAddOperationStaffAction,
  deleteOperationStaffAction,
  setDeleteOperationStaffAction,
  getAllAssignedWorkerListAction,
  setAllAssignedWorkerListAction,
  addAssignWorkerAction,
  setAddAssignWorkerAction,
  getSingleOperationStaffAction,
  setSingleOperationStaffAction,
  deleteAssignWorkerAction,
  setDeleteAssignWorkerAction,
  getAllStaffAbsenceAction,
  setAllStaffAbsenceAction,
  addStaffAbsenceAction,
  setAddStaffAbsenceAction,
  getSingleStaffAbsenceAction,
  setSingleStaffAbsenceAction,
  deleteStaffAbsenceAction,
  setDeleteStaffAbsenceAction,
  getAllOperationTasksAction,
  setAllOperationTasksAction,
  getAllTaskCrewListAction,
  setAllTaskCrewListAction,
  createTaskCrewAction,
  addBlankCrewAction,
  setCreateTaskCrewAction,
  resetTaskCrewStateAction,
  setResetTaskCrewStateAction,
  getTrackWorkerDetailsAction,
  setTrackWorkerDetailsAction,
  addTrackWorkerAction,
  setAddTrackWorkerAction,
} = assignWorkerSlice.actions;

export default assignWorkerSlice.reducer;
